import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { dialogTypes, snackbarTypes } from '@/constants/alertTypes';
import {
  NEW_VIEW,
  NEW_VIEW_TEMPLATE_NAME
} from '@/constants/dashboardConstants';
import alertsUtil from '@/util/alertsUtil';
import useProject from '@API/project/useProject';
import useProjectViews from '@API/project/useProjectViews';
import useProjectViewDetails from '@API/project/useProjectViewDetails';
import { useDashboardApiContext } from '@projects/components/EMDashboardPage/useDashboardAPIContext';
import { ConfirmRegenerateModalProps } from '@shared/components/ViewSelect/modals/ConfirmRegenerateModal';
import { ViewItemData } from '@shared/components/ViewSelect/utils';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';
import { useGenerateUnsavedViewId } from '@API/project/useGenerateUnsavedViewTemplateId';

const useDefaultViewOption = ({
  isCurrentViewFrozen,
  resetDashboardView
}: {
  isCurrentViewFrozen: boolean;
  resetDashboardView: VoidFunction;
}) => {
  const dispatch = useDispatch();
  const { viewId } = useParams<{ viewId?: string }>();
  const {
    isSavingTemplate,
    isRemovingTemplate,
    removeTemplate
  } = useDashboardApiContext();
  const { data: templates = [] } = useProjectViews();
  const { data: project } = useProject();
  const unsavedTemplateId = useGenerateUnsavedViewId();

  const [modalOpened, setModalOpened] = useState(false);

  const unsavedTemplate = templates.find(
    template => template.templateId === unsavedTemplateId
  );

  const { data: unsavedViewDetails } = useProjectViewDetails(
    {
      templateId: unsavedTemplate?.templateId
    },
    { enabled: !!unsavedTemplate?.templateId }
  );

  const isDefault =
    templates.find(template => template.templateName === NEW_VIEW_TEMPLATE_NAME)
      ?.projectDefault ?? false;

  const defaultView: ViewItemData = {
    name: 'Personal view',
    frozen:
      viewId === NEW_VIEW
        ? isCurrentViewFrozen
        : unsavedViewDetails?.v3?.isFrozenView ?? false,
    id: AUTO_GENERATED_OPTION_ID,
    isAutogenerated: true,
    isDefault,
    disabledActions: {
      duplicate: viewId !== NEW_VIEW || isSavingTemplate || isRemovingTemplate
    }
  };

  const closeModal = () => setModalOpened(false);

  const onConfirmRegenerateDefaultView = () => {
    if (isRemovingTemplate) return;

    removeTemplate({
      isSelectedTemplate: viewId === NEW_VIEW,
      personalViewId: unsavedTemplate?.templateId,
      projectId: project.projectId,
      viewId: AUTO_GENERATED_OPTION_ID,
      onSuccess: () => {
        dispatch(
          alertsUtil.openSnackbarDialog(
            snackbarTypes.SUCCESS_API_RESPONSE,
            `Personal view is up to date.`
          )
        );

        if (viewId === NEW_VIEW) {
          resetDashboardView();
        }

        closeModal();
      },
      onError: e => {
        /// @ts-expect-error fix later
        const { msg } = e.response.data;

        dispatch(
          alertsUtil.openErrorDialog(
            dialogTypes.CATCH_ERROR_SAVE_TEMPLATE,
            `There was an error regenerating your personal view: ${msg}`
          )
        );
        closeModal();
      }
    });
  };

  const onRegenerateDefaultView = () => {
    setModalOpened(true);
  };

  const confirmRegenerateModalConfig: ConfirmRegenerateModalProps = {
    onConfirm: onConfirmRegenerateDefaultView,
    onClose: closeModal,
    isOpened: modalOpened,
    isLoading: isRemovingTemplate
  };

  return { confirmRegenerateModalConfig, defaultView, onRegenerateDefaultView };
};

export default useDefaultViewOption;
